import React from "react";
import { EmailStatus } from "@pulse/pulse-rpcs/lib/types/emailStatus";
import {
  EMPTY_CHARACTER,
  getFormattedDateString,
} from "@pulse/shared-components";
import {
  IconButton,
  TableProps,
  TableRowItem,
  Typography,
} from "@surya-digital/leo-reactjs-material-ui";
import { Stack } from "@mui/material";
import { TFunction } from "i18next";
import { BookOpen, Trash2, View } from "lucide-react";
import { Instance } from "mobx-state-tree";
import { RightAlignedChip } from "../../../../../components/chips/RightAlignedChip";
import { StyledEllipsisTypography } from "../../../../../components/StyledEllipsisTypography";
import { EmailCycleRecipientModel } from "../../../models/EmailCycleRecipientModel";
import { getEmailStatusChipProps } from "../../../../utils/getEmailStatusChipProps";

export enum EmailCycleRecipientsTableColumn {
  EmailAddress = "EMAIL_ADDRESS",
  Status = "STATUS",
  AddedOn = "ADDED_ON",
  Actions = "ACTIONS",
}

const COLUMN_WIDTH = {
  EMAIL_ADDRESS: "896px",
  STATUS: "160px",
  ADDED_ON: "200px",
  ACTIONS: {
    WITHOUT_REMOVE_RECIPIENT: "120px",
    ALL_BUTTON: "160px",
  },
};

export const getEmailCycleRecipientsTableHeader = (
  t: TFunction,
  hideRemoveRecipientButton: boolean,
): TableProps["header"] => [
  {
    columnId: EmailCycleRecipientsTableColumn.EmailAddress,
    label: t("emailCycles.emailAddress"),
    width: COLUMN_WIDTH.EMAIL_ADDRESS,
  },
  {
    columnId: EmailCycleRecipientsTableColumn.Status,
    label: t("emailCycles.status"),
    width: COLUMN_WIDTH.STATUS,
  },
  {
    columnId: EmailCycleRecipientsTableColumn.AddedOn,
    label: t("emailCycles.addedOn"),
    width: COLUMN_WIDTH.ADDED_ON,
    sortable: true,
  },
  {
    columnId: EmailCycleRecipientsTableColumn.Actions,
    label: EMPTY_CHARACTER,
    width: hideRemoveRecipientButton
      ? COLUMN_WIDTH.ACTIONS.WITHOUT_REMOVE_RECIPIENT
      : COLUMN_WIDTH.ACTIONS.ALL_BUTTON,
  },
];

export const getEmailCycleRecipientsTableCells = (
  t: TFunction,
  typography: Typography,
  recipient: Instance<typeof EmailCycleRecipientModel>,
  onPreviewEmailClick: (
    emailId: string,
    status: EmailStatus.EmailStatus,
  ) => void,
  onInvestigationSummaryClick: (emailId: string) => void,
  isQueued: boolean,
  onRemoveRecipientClick: (emailId: string, email: string) => void,
): TableRowItem["cells"] => [
  {
    columnId: EmailCycleRecipientsTableColumn.EmailAddress,
    data: (
      <StyledEllipsisTypography
        data={recipient.email}
        typography={typography}
        t={t}
      />
    ),
  },
  {
    columnId: EmailCycleRecipientsTableColumn.Status,
    data: (
      <RightAlignedChip {...getEmailStatusChipProps(t, recipient.status)} />
    ),
  },
  {
    columnId: EmailCycleRecipientsTableColumn.AddedOn,
    data: (
      <StyledEllipsisTypography
        data={getFormattedDateString(new Date(recipient.addedOn))}
        typography={typography}
        t={t}
      />
    ),
  },
  {
    columnId: EmailCycleRecipientsTableColumn.Actions,
    data: (
      <Stack direction="row" justifyContent="end" width="100%">
        <IconButton
          name="viewEmailCycleRecipientButton"
          size="small"
          icon={<View />}
          variant="plain-neutral"
          onClick={(): void =>
            onPreviewEmailClick(recipient.id, recipient.status)
          }
          hoverColor="buttonOnSurfaceIcon"
        />
        <IconButton
          name="viewEmailCycleRecipientResponseButton"
          size="small"
          icon={<BookOpen />}
          variant="plain-neutral"
          onClick={(): void => onInvestigationSummaryClick(recipient.id)}
          hoverColor="buttonOnSurfaceIcon"
        />
        {isQueued && (
          <IconButton
            name="removeEmailCycleRecipientButton"
            size="small"
            icon={<Trash2 />}
            variant="plain-color"
            onClick={(): void =>
              onRemoveRecipientClick(recipient.id, recipient.email)
            }
            color="destructive"
          />
        )}
      </Stack>
    ),
  },
];
